import React, { forwardRef, useCallback, useRef, useState } from 'react';
import { motion } from 'framer-motion';

const FONTS = {
  display: "'Space Grotesk', sans-serif",
  sans: "'Inter', system-ui, sans-serif",
  mono: "'JetBrains Mono', monospace",
};

const ANIMATION_VARIANTS = {
  fade: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: 0.3 },
  },
  'slide-left': {
    initial: { opacity: 0, x: 40 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -40 },
    transition: { duration: 0.3 },
  },
  'slide-right': {
    initial: { opacity: 0, x: -40 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: 40 },
    transition: { duration: 0.3 },
  },
  'zoom-in': {
    initial: { opacity: 0, scale: 0.9 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 1.05 },
    transition: { duration: 0.3 },
  },
  none: {
    initial: {},
    animate: {},
    exit: {},
    transition: { duration: 0 },
  },
};

// Default element positions (percentage-based) when not customised.
// 'content' groups headline + body in a flex column so they never overlap.
const DEFAULT_POSITIONS = {
  content:   { x: 50, y: 45 },
  watermark: { x: 14, y: 92 },
  counter:   { x: 88, y: 92 },
};

/**
 * SlideCanvas — live WYSIWYG preview of a single slide rendered as styled HTML.
 * Used both for the visible preview and for off-screen PNG capture via html-to-image.
 *
 * Props:
 *  - slide          : { headline, bodyText, background, textColor, backgroundImage, type, elementPositions }
 *  - slideIndex     : current 0-based index
 *  - totalSlides    : total count (for counter)
 *  - format         : 'post' (1:1) | 'story' (9:16)
 *  - font           : 'display' | 'sans' | 'mono'
 *  - channelHandle  : string (e.g. '@mypage') shown as watermark
 *  - previewSize    : number (px) for the preview width (default 480)
 *  - captureMode    : boolean – when true render at 1080px for PNG export (no animations)
 *  - onOverlayMove  : (overlayIndex, {x, y}) – called when user drags an overlay image
 *  - onElementMove  : (elementType, {x, y}) – called when user drags a text/footer element
 */
const SlideCanvas = forwardRef(function SlideCanvas(
  {
    slide,
    slideIndex = 0,
    totalSlides = 1,
    format = 'post',
    font = 'display',
    channelHandle = '',
    previewSize = 480,
    captureMode = false,
    onOverlayMove,
    onElementMove,
  },
  ref
) {
  // Unified drag state — type: 'overlay' | 'headline' | 'bodyText' | 'watermark' | 'counter'
  const containerRef = useRef(null);
  const draggingRef = useRef(null); // { type, idx (overlay only), startX, startY, origX, origY }
  const [draggingType, setDraggingType] = useState(null); // visual highlight key

  const canDragElements = !captureMode && !!onElementMove;
  const canDragOverlays = !captureMode && !!onOverlayMove;

  // --- Helpers -------------------------------------------------------------
  const getPos = useCallback((type) => {
    const stored = slide?.elementPositions?.[type];
    const def = DEFAULT_POSITIONS[type];
    return { x: stored?.x ?? def.x, y: stored?.y ?? def.y };
  }, [slide?.elementPositions]);

  // --- Pointer handlers ----------------------------------------------------
  const handleOverlayPointerDown = useCallback((e, idx, ov) => {
    if (!canDragOverlays) return;
    e.preventDefault();
    e.stopPropagation();
    draggingRef.current = { type: 'overlay', idx, startX: e.clientX, startY: e.clientY, origX: ov.x ?? 50, origY: ov.y ?? 50 };
    setDraggingType(`overlay-${idx}`);
    e.target.setPointerCapture(e.pointerId);
  }, [canDragOverlays]);

  const handleElementPointerDown = useCallback((e, type) => {
    if (!canDragElements) return;
    e.preventDefault();
    e.stopPropagation();
    const pos = getPos(type);
    draggingRef.current = { type, startX: e.clientX, startY: e.clientY, origX: pos.x, origY: pos.y };
    setDraggingType(type);
    // Capture on the closest element that received the event
    const target = e.currentTarget || e.target;
    target.setPointerCapture(e.pointerId);
  }, [canDragElements, getPos]);

  const handlePointerMove = useCallback((e) => {
    const drag = draggingRef.current;
    if (!drag || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const dx = ((e.clientX - drag.startX) / rect.width) * 100;
    const dy = ((e.clientY - drag.startY) / rect.height) * 100;
    const newX = Math.max(0, Math.min(100, drag.origX + dx));
    const newY = Math.max(0, Math.min(100, drag.origY + dy));
    const pos = { x: Math.round(newX), y: Math.round(newY) };
    if (drag.type === 'overlay') {
      onOverlayMove?.(drag.idx, pos);
    } else {
      onElementMove?.(drag.type, pos);
    }
  }, [onOverlayMove, onElementMove]);

  const handlePointerUp = useCallback(() => {
    draggingRef.current = null;
    setDraggingType(null);
  }, []);

  if (!slide) return null;

  const isStory = format === 'story';
  const width = captureMode ? 1080 : previewSize;
  const height = isStory ? width * (16 / 9) : width;

  const bgColor = slide.background || '#1a1a2e';
  const textColor = slide.textColor || '#ffffff';
  const fontFamily = FONTS[font] || FONTS.display;
  const hasImage = !!slide.backgroundImage;

  // Scale font sizes relative to width
  const scale = width / 1080;
  const headlineSize = Math.round((isStory ? 140 : 120) * scale);
  const bodySize = Math.round((font === 'mono' ? 24 : 40) * scale);
  const watermarkSize = Math.round(22 * scale);
  const counterSize = Math.round(22 * scale);

  const containerStyle = {
    width,
    height,
    position: 'relative',
    overflow: 'hidden',
    backgroundColor: hasImage ? 'transparent' : bgColor,
    color: textColor,
    fontFamily,
    borderRadius: captureMode ? 0 : 12,
  };

  // Positions for each element
  const contentPos = getPos('content');
  const watermarkPos = getPos('watermark');
  const counterPos = getPos('counter');

  // Shared style builder for a draggable element wrapper
  const draggableStyle = (type, pos, extra = {}) => ({
    position: 'absolute',
    left: `${pos.x}%`,
    top: `${pos.y}%`,
    transform: 'translate(-50%, -50%)',
    zIndex: 10,
    pointerEvents: canDragElements ? 'auto' : 'none',
    cursor: canDragElements ? 'grab' : 'default',
    userSelect: 'none',
    outline: draggingType === type ? '2px solid rgba(99,102,241,0.7)' : 'none',
    outlineOffset: 2,
    borderRadius: 4,
    ...extra,
  });

  // Animation wrapper — wraps each text element for slide transitions
  const Wrapper = captureMode ? 'div' : motion.div;
  const animType = slide.animationType || 'fade';
  const animVariant = ANIMATION_VARIANTS[animType] || ANIMATION_VARIANTS.fade;
  const wrapperProps = captureMode
    ? {}
    : {
        key: `slide-${slideIndex}`,
        initial: animVariant.initial,
        animate: animVariant.animate,
        exit: animVariant.exit,
        transition: animVariant.transition,
      };

  return (
    <div
      ref={(el) => {
        containerRef.current = el;
        if (typeof ref === 'function') ref(el);
        else if (ref) ref.current = el;
      }}
      style={containerStyle}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerUp}
    >
      {/* Background image */}
      {hasImage && (
        <img
          src={slide.backgroundImage}
          alt=""
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
          crossOrigin="anonymous"
        />
      )}

      {/* Overlay images */}
      {(slide.overlayImages || []).map((ov, i) => (
        <img
          key={i}
          src={ov.url}
          alt=""
          style={{
            position: 'absolute',
            left: `${ov.x ?? 50}%`,
            top: `${ov.y ?? 50}%`,
            width: `${ov.width ?? 30}%`,
            height: 'auto',
            opacity: ov.opacity ?? 1,
            transform: 'translate(-50%, -50%)',
            zIndex: 5,
            pointerEvents: canDragOverlays ? 'auto' : 'none',
            cursor: canDragOverlays ? 'grab' : 'default',
            userSelect: 'none',
            outline: draggingType === `overlay-${i}` ? '2px solid rgba(99,102,241,0.7)' : 'none',
          }}
          crossOrigin="anonymous"
          draggable={false}
          onPointerDown={(e) => handleOverlayPointerDown(e, i, ov)}
        />
      ))}

      {/* Decorative blur overlays (only when no background image) */}
      {!hasImage && (
        <>
          <div
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              width: width * 0.5,
              height: width * 0.5,
              marginRight: -width * 0.2,
              marginTop: -width * 0.2,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.05)',
              filter: `blur(${Math.round(60 * scale)}px)`,
              pointerEvents: 'none',
            }}
          />
          <div
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              width: width * 0.5,
              height: width * 0.5,
              marginLeft: -width * 0.2,
              marginBottom: -width * 0.2,
              borderRadius: '50%',
              background: 'rgba(0,0,0,0.1)',
              filter: `blur(${Math.round(60 * scale)}px)`,
              pointerEvents: 'none',
            }}
          />
        </>
      )}

      {/* ── Content (headline + body) ── */}
      {(slide.headline || slide.bodyText) && (
        <div
          style={draggableStyle('content', contentPos, { width: '86%', maxWidth: '86%' })}
          onPointerDown={(e) => handleElementPointerDown(e, 'content')}
        >
          <Wrapper {...wrapperProps} style={{ display: 'flex', flexDirection: 'column' }}>
            {slide.headline && (
              <h2
                style={{
                  fontSize: headlineSize,
                  fontWeight: 900,
                  textTransform: 'uppercase',
                  lineHeight: 0.9,
                  margin: 0,
                  marginBottom: slide.bodyText ? Math.round(24 * scale) : 0,
                  letterSpacing: font === 'mono' ? '-0.04em' : undefined,
                }}
              >
                {slide.headline}
              </h2>
            )}
            {slide.bodyText && (
              <p
                style={{
                  fontSize: bodySize,
                  fontWeight: 500,
                  opacity: 0.9,
                  margin: 0,
                  textTransform: font === 'mono' ? 'uppercase' : undefined,
                  letterSpacing: font === 'mono' ? '0.1em' : undefined,
                }}
              >
                {slide.bodyText}
              </p>
            )}
          </Wrapper>
        </div>
      )}

      {/* ── Watermark ── */}
      {channelHandle && (
        <div
          style={draggableStyle('watermark', watermarkPos)}
          onPointerDown={(e) => handleElementPointerDown(e, 'watermark')}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 * scale, whiteSpace: 'nowrap' }}>
            <div
              style={{
                width: Math.round(32 * scale),
                height: Math.round(32 * scale),
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: Math.round(14 * scale),
              }}
            >
              📷
            </div>
            <span
              style={{
                fontSize: watermarkSize,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                opacity: 0.7,
              }}
            >
              {channelHandle}
            </span>
          </div>
        </div>
      )}

      {/* ── Slide Counter ── */}
      <div
        style={draggableStyle('counter', counterPos)}
        onPointerDown={(e) => handleElementPointerDown(e, 'counter')}
      >
        <span
          style={{
            fontSize: counterSize,
            fontFamily: "'JetBrains Mono', monospace",
            opacity: 0.5,
            whiteSpace: 'nowrap',
          }}
        >
          {slideIndex + 1} / {totalSlides}
        </span>
      </div>
    </div>
  );
});

/** Off-screen render container for all slides (used by ZIP export). */
export function SlideCanvasOffscreen({ slides, format, font, channelHandle, refs }) {
  return (
    <div style={{ position: 'fixed', left: -9999, top: 0, pointerEvents: 'none' }}>
      {slides.map((slide, i) => (
        <SlideCanvas
          key={slide._id || i}
          ref={(el) => { if (refs) refs.current[i] = el; }}
          slide={slide}
          slideIndex={i}
          totalSlides={slides.length}
          format={format}
          font={font}
          channelHandle={channelHandle}
          captureMode
        />
      ))}
    </div>
  );
}

export default SlideCanvas;
